import type { Category, Product } from "./types";

export const categories: Category[] = [
  {
    id: 29,
    slug: "tile-stone-adhesive",
    title: "Tile & Stone Adhesive",
    summary:
      "High bonding strength, flexibility, and resistance for tiles and natural stone.",
    description:
      "These adhesives are formulated for secure, long-lasting bonding of tiles and stone. Use them where you need strength on floors, walls, and wet areas without compromising finish.",
    image: "/images/products/tile-adhesive.webp",
  },
  {
    id: 30,
    slug: "stone-care-products",
    title: "Stone Care Products",
    summary:
      "Protect, maintain, and restore natural and engineered stone surfaces.",
    description:
      "Stone is a statement of strength and finish. Our stone-care range is designed to protect, maintain, and enhance marble, granite, and other stone without dulling the natural look.",
    image: "/images/products/stone-care.png",
  },
  {
    id: 31,
    slug: "cleaners",
    title: "Cleaners",
    summary:
      "Lift dirt, grime, cement residue, and stains from stone and tile.",
    description:
      "Our cleaners are made to remove construction deposits and everyday soiling from stone and tile, restoring the original appearance without aggressive damage to the surface.",
    image: "/images/products/cleaners.jpg",
  },
  {
    id: 33,
    slug: "waterproofing-construction-chemicals",
    title: "Waterproofing & Construction Chemicals",
    summary:
      "Protection against water ingress for roofs, bathrooms, basements, and structures.",
    description:
      "Kasa Chemicals supplies waterproofing and construction chemicals that help keep installations dry and durable — from terraces and roofs to bathrooms, basements, and foundations.",
    image: "/images/products/waterproofing.jpg",
  },
];

export const products: Product[] = [
  {
    id: 125,
    slug: "adresh-307",
    categorySlug: "tile-stone-adhesive",
    title: "Adresh 307",
    summary: "Tile and stone adhesive for secure indoor fixing.",
    description: [
      "Adresh 307 is part of the Kasa tile and stone adhesive range, formulated for high bonding strength on ceramic, vitrified, and stone installations.",
      "Use it where you need a dependable bed for floors and walls. Follow surface preparation and mixing guidance on the pack for a long-lasting bond.",
    ],
    image: "/images/products/adresh-307.png",
    featured: true,
  },
  {
    id: 126,
    slug: "tile-adhesive-k60",
    categorySlug: "tile-stone-adhesive",
    title: "Tile Adhesive K60",
    summary: "Flexible adhesive for demanding tile and stone work.",
    description: [
      "K60 is specified where flexibility and resistance matter — larger-format tiles, stone, and areas that see movement or moisture.",
      "It belongs to the same adhesive family used by contractors who need a consistent, site-ready mix.",
    ],
    image: "/images/products/adhesive-k60.jpg",
    featured: true,
  },
  {
    id: null,
    slug: "adhesive-315",
    categorySlug: "tile-stone-adhesive",
    title: "Adhesive 315",
    summary: "High-performance adhesive for tile and stone applications.",
    description: [
      "Adhesive 315 is a high-performance option for secure, long-lasting bonding of tiles and stone materials.",
      "Choose it when the job needs extra hold without a complicated application process.",
    ],
    image: "/images/products/adhesive-315.jpg",
    featured: false,
  },
  {
    id: 124,
    slug: "stone-care-range",
    categorySlug: "stone-care-products",
    title: "Stone Care Range",
    summary: "Protect and enhance natural and engineered stone.",
    description: [
      "Kasa Chemicals, based in Jaipur, manufactures stone care and construction chemicals with a range designed to protect, maintain, and enhance stone surfaces.",
      "The range includes stain removers, cement removers, and mastic for filling cracks and joints in natural stone and tiles.",
    ],
    image: "/images/products/stone-care-pack.png",
    featured: true,
  },
  {
    id: null,
    slug: "kasa-stain-remover",
    categorySlug: "stone-care-products",
    title: "Kasa Stain Remover",
    summary: "Removes tough stains without harming the stone finish.",
    description: [
      "Formulated to eliminate tough stains from stone surfaces without causing damage, so the stone's natural appearance is preserved.",
    ],
    image: "/images/products/stone-care.png",
    featured: false,
  },
  {
    id: null,
    slug: "kasa-cement-remover",
    categorySlug: "cleaners",
    title: "Kasa Cement Remover",
    summary: "Clears cement residue and construction deposits from stone.",
    description: [
      "Designed to remove cement residues and construction-related deposits from stone, restoring the original look after installation work.",
    ],
    image: "/images/products/cleaners.jpg",
    featured: true,
  },
  {
    id: null,
    slug: "kasa-wr-plus",
    categorySlug: "waterproofing-construction-chemicals",
    title: "Kasa WR+",
    summary: "Waterproofing protection against water ingress.",
    description: [
      "Kasa WR+ is part of our waterproofing line, used to protect stone installations and building surfaces from water ingress and seasonal damage.",
    ],
    image: "/images/products/wr-plus.png",
    featured: true,
  },
  {
    id: null,
    slug: "kasa-sbr-1103",
    categorySlug: "waterproofing-construction-chemicals",
    title: "Kasa SBR 1103",
    summary: "SBR-based waterproofing for durable installations.",
    description: [
      "Kasa SBR 1103 supports robust protection in waterproofing systems, helping keep roofs, wet rooms, and structures dry over time.",
    ],
    image: "/images/products/sbr.png",
    featured: false,
  },
  {
    id: null,
    slug: "kasa-flexi-coat",
    categorySlug: "waterproofing-construction-chemicals",
    title: "Kasa Flexi Coat",
    summary: "Flexible coating for terraces, roofs, and wet areas.",
    description: [
      "Kasa Flexi Coat is used where a flexible waterproof layer is needed — terraces, roofs, bathrooms, and other areas exposed to moisture.",
    ],
    image: "/images/products/flexi-coat.png",
    featured: false,
  },
];

export function getCategory(slug: string) {
  return categories.find((category) => category.slug === slug);
}

export function getProductsByCategory(slug: string) {
  return products.filter((product) => product.categorySlug === slug);
}

export function getProduct(categorySlug: string, productSlug: string) {
  return products.find(
    (product) =>
      product.categorySlug === categorySlug && product.slug === productSlug,
  );
}

export function getFeaturedProducts() {
  return products.filter((product) => product.featured);
}

export function getRelatedProducts(categorySlug: string, productSlug: string) {
  return getProductsByCategory(categorySlug)
    .filter((product) => product.slug !== productSlug)
    .slice(0, 3);
}
